/**
 * Combo swatches — sibling-product color switching on the Product 2026 – Bundle section.
 *
 * Each combo color is its own Shopify product with its own URL, images, variants and inventory.
 * The swatches link those siblings together (product.metafields.custom.combo_siblings), and this
 * script swaps the whole rendered section for the clicked sibling's, so the title, gallery,
 * variant options, availability, price, description and accordions all follow the color — then
 * pushes the sibling's URL so every color stays a real, shareable, indexable page.
 *
 * The section markup is fetched from the sibling's own page, so nothing here has to know which
 * fields exist; the fallback on any failure is a plain navigation to that URL.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-product-bundle-2026]';
  var requestToken = 0;

  function parseSectionRoot(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    return {
      root: doc.querySelector(ROOT_SELECTOR),
      title: doc.title || ''
    };
  }

  /**
   * Prefer the Section Rendering API (just this section, not the whole page). It only works when
   * the sibling's template happens to use the same section id, so fall back to the full page.
   */
  function fetchSectionRoot(url, sectionId) {
    var sectionRequest = Promise.resolve(null);

    if (sectionId) {
      sectionRequest = fetch(url + '?section_id=' + encodeURIComponent(sectionId))
        .then(function (response) {
          return response.ok ? response.text() : null;
        })
        .then(function (html) {
          if (!html) {
            return null;
          }

          var parsed = parseSectionRoot(html);

          return parsed.root ? parsed : null;
        })
        .catch(function () {
          return null;
        });
    }

    return sectionRequest.then(function (parsed) {
      if (parsed) {
        return parsed;
      }

      return fetch(url)
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Combo swatch request failed: ' + response.status);
          }

          return response.text();
        })
        .then(function (html) {
          var fullPage = parseSectionRoot(html);

          if (!fullPage.root) {
            throw new Error('Combo swatch response had no product section');
          }

          return fullPage;
        });
    });
  }

  function swapSection(root, parsed, url) {
    root.innerHTML = parsed.root.innerHTML;
    root.setAttribute('data-product-url', url);

    // initProductBundle2026 no-ops on an already-initialized root, and the swapped-in markup
    // needs its buy box, option buttons and gallery bound from scratch.
    root.removeAttribute('data-product-bundle-2026-initialized');

    if (window.ProductBundle2026 && typeof window.ProductBundle2026.init === 'function') {
      window.ProductBundle2026.init(root);
    }

    var heading = root.querySelector('.ProductBundle2026__Title');

    if (parsed.title) {
      document.title = parsed.title;
    } else if (heading) {
      document.title = heading.textContent.trim();
    }

    // The swapped-in gallery images are lazysizes placeholders, and Judge.me badges are rendered
    // client-side — both need a nudge for markup that arrived after their initial pass.
    if (window.lazySizes && window.lazySizes.loader) {
      window.lazySizes.loader.checkElems();
    }

    if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
      window.jdgm.customizeBadges();
    }
  }

  function showSibling(root, url, options) {
    var token = ++requestToken;
    var sectionId = root.getAttribute('data-section-id');

    root.classList.add('is-comboLoading');

    return fetchSectionRoot(url, sectionId)
      .then(function (parsed) {
        // A newer swatch was clicked while this was in flight — let that one win.
        if (token !== requestToken) {
          return;
        }

        swapSection(root, parsed, url);
        root.classList.remove('is-comboLoading');

        if (!options || options.pushState !== false) {
          window.history.pushState({ comboUrl: url }, '', url);
        }
      })
      .catch(function (error) {
        console.error('Combo swatch error, falling back to navigation:', error);
        window.location.href = url;
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-combo-swatches] [data-combo-url]');

    if (!button || button.classList.contains('is-active')) {
      return;
    }

    var root = button.closest(ROOT_SELECTOR);
    var url = button.getAttribute('data-combo-url');

    if (!root || !url) {
      return;
    }

    event.preventDefault();

    // Move the selection immediately so the swatch responds on tap; the fetched markup carries
    // the authoritative active state a moment later.
    var container = button.closest('[data-combo-swatches]');
    var label = container.querySelector('[data-combo-selected-label]');

    Array.prototype.forEach.call(container.querySelectorAll('[data-combo-url]'), function (other) {
      other.classList.toggle('is-active', other === button);
    });

    if (label && button.getAttribute('data-combo-label')) {
      label.textContent = button.getAttribute('data-combo-label');
    }

    showSibling(root, url);
  });

  window.addEventListener('popstate', function () {
    var root = document.querySelector(ROOT_SELECTOR);

    if (!root) {
      return;
    }

    var url = window.location.pathname + window.location.search;

    if (url === root.getAttribute('data-product-url')) {
      return;
    }

    showSibling(root, url, { pushState: false });
  });
})();
